import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import './LinkedInOptimizerLanding.css';

const HERO_IMG = '/images/linkedin-optimizer/hero-li-stage.png';
const HEADLINE_IMG = '/images/linkedin-optimizer/headline-rewrite.png';
const RADAR_IMG = '/images/linkedin-optimizer/keyword-radar.png';
const MAGNET_IMG = '/images/linkedin-optimizer/recruiter-magnet.png';

/* ---------- Three.js view-counter spiral canvas ---------- */
const ProfileMagnetCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 75;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(0,255,255,0.95)');
      g.addColorStop(0.7, 'rgba(74,144,226,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // 60 inbound "viewer" particles spiraling toward center
    const COUNT = 60;
    const positions = new Float32Array(COUNT * 3);
    const angles = new Float32Array(COUNT);
    const radii = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      angles[i] = Math.random() * Math.PI * 2;
      radii[i] = 12 + Math.random() * 50;
      speeds[i] = 0.005 + Math.random() * 0.012;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.PointsMaterial({
      size: 2.4, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const swarm = new THREE.Points(geo, mat);
    scene.add(swarm);

    // ambient particle field
    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 180;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 110;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.0, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.45,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // central profile orb (cyan wireframe)
    const orbGeo = new THREE.IcosahedronGeometry(8, 1);
    const orb = new THREE.Mesh(orbGeo, new THREE.MeshBasicMaterial({
      color: 0x00ffff, wireframe: true, transparent: true, opacity: 0.45,
    }));
    scene.add(orb);

    let raf;
    let mx = 0, my = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      const np = geo.attributes.position.array;
      for (let i = 0; i < COUNT; i++) {
        radii[i] -= speeds[i] * 6;
        if (radii[i] < 6) {
          radii[i] = 60;
          angles[i] = Math.random() * Math.PI * 2;
        }
        np[i * 3]     = Math.cos(angles[i]) * radii[i];
        np[i * 3 + 1] = Math.sin(angles[i]) * radii[i] * 0.6;
        np[i * 3 + 2] = (Math.random() - 0.5) * 4;
      }
      geo.attributes.position.needsUpdate = true;

      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 55) fp[i * 3 + 1] = -55;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      orb.rotation.x += 0.005;
      orb.rotation.y += 0.007;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      geo.dispose(); mat.dispose();
      fieldGeo.dispose(); fieldMat.dispose();
      orbGeo.dispose(); orb.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="lol-hero-canvas" aria-hidden="true" />;
};

/* ---------- Headline rewrite typewriter demo ---------- */
const HEADLINE_BEFORE = 'Software Engineer @ Acme | Passionate about technology';
const HEADLINE_AFTER = 'Senior Backend Engineer at Acme · Built fault-tolerant systems handling 50M req/day · Mentor to 6 engineers';

const HeadlineRewriteDemo = () => {
  const ref = useRef(null);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && phase === 'idle') setPhase('typing'); }),
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'typing') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(HEADLINE_AFTER.slice(0, i));
      if (i >= HEADLINE_AFTER.length) { clearInterval(id); setPhase('done'); }
    }, 22);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <section className="lol-section" ref={ref}>
      <div className="lol-section-grid">
        <div className="lol-section-text">
          <span className="lol-eyebrow">HEADLINE REWRITE</span>
          <h2 className="lol-h2">
            From "Passionate about tech" to <span className="lol-grad-text">"recruiter-magnet headline"</span>.
          </h2>
          <p className="lol-lead">
            Your LinkedIn headline is the single most-searched field on the platform. We rewrite
            it with role + seniority + quantified impact + a memorable hook — packed with the
            keywords recruiters actually search for.
          </p>
          <ul className="lol-bullet-list">
            <li><span className="lol-dot" />Recruiter-keyword density tuned to your role</li>
            <li><span className="lol-dot" />Quantified hook from your real experience</li>
            <li><span className="lol-dot" />Stays within LinkedIn's 220-char limit</li>
          </ul>
        </div>

        <div className="lol-rewrite-card">
          <div className="lol-rewrite-block">
            <span className="lol-rewrite-label lol-tag-red">Before</span>
            <p className="lol-rewrite-before">{HEADLINE_BEFORE}</p>
          </div>

          <div className="lol-rewrite-arrow">↓</div>

          <div className="lol-rewrite-block lol-rewrite-after-block">
            <span className="lol-rewrite-label lol-tag-mint">After (AI rewrite)</span>
            <p className="lol-rewrite-after">
              {typed}
              {phase === 'typing' && <span className="lol-caret">|</span>}
            </p>
            {phase === 'done' && (
              <div className="lol-rewrite-meta">
                <span>+5× recruiter views</span>
                <span>+38% search hits</span>
                <span>STAR-formatted</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Profile score gauge demo ---------- */
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1600;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};

const ProfileScoreDemo = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const score = useCounter(91, start);
  const circumference = 2 * Math.PI * 70;
  const offset = circumference * (1 - score / 100);

  const sections = [
    { name: 'Headline', val: useCounter(94, start) },
    { name: 'Summary', val: useCounter(88, start) },
    { name: 'Experience', val: useCounter(92, start) },
    { name: 'Skills', val: useCounter(89, start) },
  ];

  return (
    <section className="lol-section" ref={ref}>
      <div className="lol-section-grid lol-flip">
        <div className="lol-score-card">
          <svg className="lol-score-svg" viewBox="0 0 160 160">
            <defs>
              <linearGradient id="lolScoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00ffff" />
                <stop offset="60%" stopColor="#4a90e2" />
                <stop offset="100%" stopColor="#6fffd1" />
              </linearGradient>
            </defs>
            <circle cx="80" cy="80" r="70" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
            <circle
              cx="80" cy="80" r="70"
              stroke="url(#lolScoreGrad)"
              strokeWidth="10" fill="none" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 80 80)"
              style={{ transition: 'stroke-dashoffset 0.4s linear', filter: 'drop-shadow(0 0 8px rgba(0,255,255,0.5))' }}
            />
          </svg>
          <div className="lol-score-label">
            <div className="lol-score-num">{score}</div>
            <div className="lol-score-sub">/ 100 PROFILE STRENGTH</div>
          </div>
          <div className="lol-score-rows">
            {sections.map((s) => (
              <div key={s.name} className="lol-score-row">
                <span>{s.name}</span>
                <div className="lol-score-bar"><div className="lol-score-bar-fill" style={{ width: `${s.val}%` }} /></div>
                <span className="lol-score-val">{s.val}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lol-section-text">
          <span className="lol-eyebrow">PROFILE STRENGTH SCORE</span>
          <h2 className="lol-h2">
            Single number. <span className="lol-grad-text">Per-section breakdown.</span>
          </h2>
          <p className="lol-lead">
            We grade every section against what LinkedIn's algorithm actually ranks: headline
            density, summary length + structure, skills coverage, experience metrics, profile
            completeness. You see exactly what to fix and how much it'll move the needle.
          </p>
          <ul className="lol-bullet-list">
            <li><span className="lol-dot" />Per-section sub-scores with priority order</li>
            <li><span className="lol-dot" />Specific edits, not generic "add more"</li>
            <li><span className="lol-dot" />Re-score instantly after each change</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Drop your profile URL', body: 'Paste your LinkedIn URL or upload an exported profile PDF.' },
  { title: 'AI scans every section', body: 'Headline, About, Experience, Skills — graded against what recruiters search for.' },
  { title: 'Targets your role', body: 'Tell us the role you want and the AI tailors keyword density to it.' },
  { title: 'Per-section rewrites', body: 'Headline, summary, experience bullets — each with a copy-paste-ready output.' },
  { title: 'Profile strength score', body: 'A single 0–100 number with per-section breakdown and what to fix first.' },
  { title: 'Ship & track', body: 'Update your profile, re-scan, watch the score climb — and the recruiter views with it.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="lol-section" ref={ref}>
      <div className="lol-section-head">
        <span className="lol-eyebrow">HOW IT WORKS</span>
        <h2 className="lol-h2">
          Six steps. <span className="lol-grad-text">5× more recruiter views.</span>
        </h2>
        <p className="lol-lead lol-center">
          From "I never get LinkedIn DMs" to "I had to turn off notifications" — measurable in days.
        </p>
      </div>
      <div className="lol-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`lol-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="lol-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`lol-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 5, suffix: '×', label: 'More recruiter views' },
  { val: 91, suffix: '%', label: 'Average score after fix' },
  { val: 7, suffix: 's', label: 'Recruiter spends per profile' },
  { val: 180, suffix: 'K+', label: 'Profiles optimized' },
];
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="lol-stat">
      <div className="lol-stat-num">{v}{stat.suffix}</div>
      <div className="lol-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="lol-stats-band" ref={ref}>
      <div className="lol-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'How does it know what recruiters search for?', a: 'We\'ve indexed thousands of LinkedIn recruiter Boolean searches by role, seniority, and industry — and surface the keyword patterns that actually return results. Your headline and skills get tuned to match.' },
  { q: 'Will my profile sound like AI wrote it?', a: 'No. The rewrites preserve your voice — we keep your real experience, projects, and metrics, just structure them for maximum recruiter resonance. You approve every line.' },
  { q: 'Do I have to give you my LinkedIn login?', a: 'No. Just paste the public URL or export your profile as PDF. We never ask for credentials, never log into your account, never automate posting.' },
  { q: 'What\'s the realistic improvement?', a: 'Most users see a 3–5× increase in recruiter views within two weeks of applying the rewrites. Your mileage varies by role demand and starting baseline.' },
  { q: 'Can I tailor for a specific job?', a: 'Yes — paste a JD and we\'ll re-tune your headline + summary to match that exact role\'s search patterns, while keeping your profile coherent for general recruiter searches.' },
  { q: 'Does it support multiple roles / pivots?', a: 'Yes. The optimizer handles career pivots cleanly — we know how to position transitional experience without making you look unfocused.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="lol-section">
      <div className="lol-section-head">
        <span className="lol-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="lol-h2">Everything you wanted to ask</h2>
      </div>
      <div className="lol-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`lol-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="lol-faq-q">
              <span>{f.q}</span>
              <span className="lol-faq-chev">+</span>
            </div>
            <div className="lol-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const LinkedInOptimizerLanding = () => {
  const navigate = useNavigate();
  // "Optimize my LinkedIn" now opens the dedicated LinkedIn Studio page instead
  // of scrolling to an inline tool on the landing.
  const goStudio = useCallback(() => navigate('/linkedin-studio'), [navigate]);
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="lol-page">
      <Seo
        title="LinkedIn Profile Optimizer"
        description="Optimize your LinkedIn profile for recruiters and search with AI-driven headline, About, and keyword suggestions."
        path="/LinkedInOptimizer"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="lol-hero" ref={heroRef}>
        <ProfileMagnetCanvas />
        <div className="lol-hero-bg" />
        <div className="lol-hero-halo" aria-hidden="true" />
        <img src={HERO_IMG} alt="" className="lol-hero-img" loading="eager" />

        <div className="lol-hero-content">
          <span className="lol-hero-pill">
            <span className="lol-pill-dot" /> AI rewrites · profile score · 5× recruiter views
          </span>
          <h1 className="lol-h1">
            Become a <span className="lol-grad-text">recruiter magnet</span> on LinkedIn.
          </h1>
          <p className="lol-hero-sub">
            Paste your profile URL, get back AI-rewritten headline, summary, and experience —
            tuned to the keywords recruiters actually search for. A single profile-strength score
            tells you exactly what to fix.
          </p>

          <div className="lol-hero-cta">
            <button className="lol-btn lol-btn-primary" onClick={goStudio}>
              <span>Optimize my LinkedIn</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="lol-btn lol-btn-ghost" onClick={() => navigate('/resume-builder')}>
              Build resume first
            </button>
          </div>

          <div className="lol-hero-checks">
            <span>✓ Headline + summary rewrites</span>
            <span>✓ Profile-strength score</span>
            <span>✓ Never logs into your account</span>
          </div>
        </div>

        {/* Floating glass profile card */}
        <div className="lol-floater" aria-hidden="true">
          <div className="lol-floater-card">
            <div className="lol-floater-head">
              <span className="lol-floater-dot" />
              <span>PROFILE SCORE</span>
            </div>
            <div className="lol-floater-num">91<span>/100</span></div>
            <div className="lol-floater-bar"><div className="lol-floater-bar-fill" /></div>
            <div className="lol-floater-meta">
              <span>Headline ✓ 94</span>
              <span>Summary ✓ 88</span>
              <span>Skills ✓ 89</span>
            </div>
          </div>
          <div className="lol-floater-glow" />
        </div>
      </section>

      <HeadlineRewriteDemo />
      <ProfileScoreDemo />

      <section className="lol-section">
        <div className="lol-section-grid">
          <div className="lol-section-text">
            <span className="lol-eyebrow">RECRUITER MAGNET</span>
            <h2 className="lol-h2">
              Inbound DMs you <span className="lol-grad-text">don't ask for</span>.
            </h2>
            <p className="lol-lead">
              The LinkedIn algorithm boosts profiles with high keyword density and complete
              sections in recruiter searches. We optimize for both — so the recruiters you'd
              never have time to find start finding you instead.
            </p>
            <ul className="lol-bullet-list">
              <li><span className="lol-dot" />5× more profile views in 2 weeks</li>
              <li><span className="lol-dot" />Higher rank in recruiter Boolean searches</li>
              <li><span className="lol-dot" />Better connection-acceptance rate</li>
            </ul>
          </div>
          <div className="lol-img-frame">
            <img src={MAGNET_IMG} alt="Recruiter magnet" />
            <div className="lol-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="lol-section">
        <div className="lol-section-grid lol-flip">
          <div className="lol-img-frame">
            <img src={HEADLINE_IMG} alt="Headline rewrite" />
            <div className="lol-img-frame-glow" />
          </div>
          <div className="lol-section-text">
            <span className="lol-eyebrow">HEADLINE-FIRST</span>
            <h2 className="lol-h2">
              The line that <span className="lol-grad-text">decides everything</span>.
            </h2>
            <p className="lol-lead">
              Your headline is the single most-searched, most-displayed field on LinkedIn. It
              shows in search results, on connection requests, on every comment you make. We
              treat it like the most valuable real estate in your profile — because it is.
            </p>
          </div>
        </div>
      </section>

      <section className="lol-section">
        <div className="lol-section-grid">
          <div className="lol-section-text">
            <span className="lol-eyebrow">KEYWORD COVERAGE</span>
            <h2 className="lol-h2">
              See every keyword <span className="lol-grad-text">you're missing</span>.
            </h2>
            <p className="lol-lead">
              We analyse your target role's typical recruiter Boolean searches and surface the
              skills, tools, and methodologies you should be ranking for. Each missing keyword
              comes with a suggestion for where naturally to weave it in.
            </p>
          </div>
          <div className="lol-img-frame">
            <img src={RADAR_IMG} alt="Keyword radar" />
            <div className="lol-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="lol-cta-final">
        <div className="lol-cta-card">
          <div className="lol-cta-glow" />
          <h2 className="lol-h2">
            Stop being <span className="lol-grad-text">invisible</span> on LinkedIn.
          </h2>
          <p>One scan. One optimization pass. 5× more recruiter views.</p>
          <div className="lol-hero-cta center">
            <button className="lol-btn lol-btn-primary" onClick={goStudio}>
              <span>Optimize my LinkedIn</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="lol-btn lol-btn-ghost" onClick={() => navigate('/resume-builder')}>Build resume first</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default LinkedInOptimizerLanding;
